import { useCallback, useEffect, useRef } from 'react';
import { AppState, Platform } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { usePosCashSessionStore } from '../../store/usePosCashSessionStore';
import {
  getMergedActiveCashSession,
  subscribeCashSessionChangeSignal,
} from '../../../cash/infra-api';
import { isDatabaseUnavailableError } from '../../../shared/localDb';
import { showInfo } from '../../../shared/ui/appToast';

/** Reintento si la consulta queda colgada (HMR, red lenta, pestaña en background). */
const CASH_SESSION_RECOVERY_MS = 8_000;
const DEDUPE_WINDOW_MS = 1000;

export function usePosCashSessionLoader(currentBranchId: string | null) {
  const applyCashSessionSnapshot = usePosCashSessionStore((state) => state.applyCashSessionSnapshot);
  const markCashSessionPendingForBranch = usePosCashSessionStore((state) => state.markCashSessionPendingForBranch);
  const cashSessionLoading = usePosCashSessionStore((state) => state.cashSessionLoading);
  const requestSeqRef = useRef(0);
  const isInitialMount = useRef(true);
  const inFlightCountRef = useRef(0);
  const lastBranchIdRef = useRef<string | null>(null);
  const lastLoadAtRef = useRef<number>(0);
  const prevBranchIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (prevBranchIdRef.current === currentBranchId) {
      return;
    }
    const prev = prevBranchIdRef.current;
    prevBranchIdRef.current = currentBranchId;
    if (prev !== undefined && prev !== currentBranchId) {
      markCashSessionPendingForBranch(currentBranchId);
    }
  }, [currentBranchId, markCashSessionPendingForBranch]);

  const refreshCashSession = useCallback(
    async (notifyOnError = false, force = false) => {
      if (!currentBranchId) {
        markCashSessionPendingForBranch(null);
        return;
      }

      if (inFlightCountRef.current > 0 && !force) {
        return;
      }

      const hydratedFor = usePosCashSessionStore.getState().cashSessionHydratedForBranchId;
      const isBackground = hydratedFor === currentBranchId;
      if (!isBackground) {
        markCashSessionPendingForBranch(currentBranchId);
      }

      const seq = ++requestSeqRef.current;
      inFlightCountRef.current++;
      lastBranchIdRef.current = currentBranchId;
      lastLoadAtRef.current = Date.now();
      try {
        const session = await getMergedActiveCashSession(currentBranchId, { force });
        if (seq !== requestSeqRef.current) {
          return;
        }
        applyCashSessionSnapshot(currentBranchId, session);
      } catch (error) {
        if (seq !== requestSeqRef.current) return;
        if (!isDatabaseUnavailableError(error)) {
          console.warn('[POS] No se pudo consultar la caja activa', error);
        }
        applyCashSessionSnapshot(currentBranchId, null);
        if (notifyOnError) {
          showInfo('No se pudo verificar la caja', 'Reintentá o revisá la conexión.');
        }
      } finally {
        inFlightCountRef.current = Math.max(0, inFlightCountRef.current - 1);
      }
    },
    [applyCashSessionSnapshot, currentBranchId, markCashSessionPendingForBranch]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (currentBranchId) {
        void refreshCashSession();
      }
      return;
    }
    if (!currentBranchId) {
      return;
    }
    void refreshCashSession();
  }, [currentBranchId, refreshCashSession]);

  useFocusEffect(
    useCallback(() => {
      if (!currentBranchId) return;
      if (
        lastBranchIdRef.current === currentBranchId &&
        Date.now() - lastLoadAtRef.current < DEDUPE_WINDOW_MS
      ) {
        return;
      }
      void refreshCashSession(false, true);
    }, [currentBranchId, refreshCashSession])
  );

  useEffect(() => {
    return subscribeCashSessionChangeSignal(() => {
      void refreshCashSession(false, true);
    });
  }, [refreshCashSession]);

  useEffect(() => {
    const refreshOnActive = () => {
      if (!currentBranchId) return;
      void refreshCashSession(false, true);
    };

    const appSub = AppState.addEventListener('change', (state) => {
      if (state === 'active') refreshOnActive();
    });

    let removeVisibility: (() => void) | undefined;
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      const onVisibility = () => {
        if (document.visibilityState === 'visible') refreshOnActive();
      };
      document.addEventListener('visibilitychange', onVisibility);
      removeVisibility = () => document.removeEventListener('visibilitychange', onVisibility);
    }

    return () => {
      appSub.remove();
      removeVisibility?.();
    };
  }, [currentBranchId, refreshCashSession]);

  useEffect(() => {
    if (!cashSessionLoading || !currentBranchId) return;
    const timer = setTimeout(() => {
      void refreshCashSession(false, true);
    }, CASH_SESSION_RECOVERY_MS);
    return () => clearTimeout(timer);
  }, [cashSessionLoading, currentBranchId, refreshCashSession]);

  return { refreshCashSession };
};
